import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Mic, Square, Download, Trash2, 
    AlertCircle, Play, Pause, RefreshCw, Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
// Assuming you have a reusable component for the payment link, 
// otherwise replace with your logic
import { Link } from 'react-router-dom'; 

const VoiceRecorder = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    // --- STATE ---
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [error, setError] = useState(null);
    const [mimeType, setMimeType] = useState('audio/webm'); // Default

    // --- REFS ---
    const mediaRecorderRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const chunksRef = useRef([]);

    // --- CLEANUP ---
    useEffect(() => {
        return () => {
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    // --- LOGIC ---

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const startRecording = async () => {
        setError(null);
        setAudioBlob(null);
        setAudioUrl(null);
        chunksRef.current = [];

        try {
            // Request Microphone Permissions
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Determine supported mime type
            let options = { mimeType: 'audio/webm' };
            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                options = { mimeType: 'audio/webm;codecs=opus' };
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                options = { mimeType: 'audio/mp4' }; // Safari
            }
            setMimeType(options.mimeType);

            const mediaRecorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: options.mimeType });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                
                // Stop all tracks to release microphone
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            
            // Start Timer
            setRecordingTime(0);
            timerIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error("Recording Error:", err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError(t('voiceRecorder.errors.permissionDenied'));
            } else {
                setError(t('voiceRecorder.errors.generic') + `: ${err.message}`);
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        }
    };

    const resetRecording = () => {
        setAudioBlob(null);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
        setError(null);
        setRecordingTime(0);
    };

    // Determine file extension for download
    const getFileExtension = () => {
        if (mimeType.includes('mp4')) return '.mp4';
        if (mimeType.includes('wav')) return '.wav';
        return '.webm'; // Standard for Chrome/Firefox
    };

    // --- ACCESS CONTROL CHECK ---
    const hasAccess = user && (user.credits > 0 || user.is_verified);

    // --- RENDER ---
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('voiceRecorder.title')}</h2>
                <p className="mt-2 text-brand-dark mb-8">{t('voiceRecorder.description')}</p>

                {!user ? (
                     // Not Logged In
                     <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 text-center">
                         <p className="mb-4">{t('voiceRecorder.loginRequired')}</p>
                         <Link to="/login" className="px-6 py-2 bg-brand-orange text-white rounded hover:bg-orange-600 transition">
                             {t('voiceRecorder.loginButton')}
                         </Link>
                     </div>
                ) : !hasAccess ? (
                    // No Credits
                    <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 text-center">
                        <AlertCircle className="mx-auto h-12 w-12 text-brand-orange mb-4" />
                        <h3 className="text-lg font-bold mb-2">{t('voiceRecorder.noCreditsTitle')}</h3>
                        <p className="text-slate-600 mb-6">{t('voiceRecorder.noCreditsDesc')}</p>
                        {/* Replace this with your Stripe Payment Component or Link */}
                        <button className="px-6 py-2 bg-brand-orange text-white rounded hover:bg-orange-600 transition">
                            {t('voiceRecorder.subscribeButton')}
                        </button>
                    </div>
                ) : (
                    // Main Tool
                    <div className="bg-white p-6 md:p-10 rounded-lg shadow-sm border border-slate-200 flex flex-col items-center justify-center min-h-[400px]">
                        
                        {/* TIMER DISPLAY */}
                        <div className={`mb-8 font-mono text-4xl font-bold transition-colors ${isRecording ? 'text-red-500 animate-pulse' : 'text-slate-700'}`}>
                            {formatTime(recordingTime)}
                        </div>

                        {/* CONTROLS */}
                        {!audioUrl ? (
                            <div className="flex flex-col items-center">
                                {!isRecording ? (
                                    <button 
                                        onClick={startRecording}
                                        className="group relative flex items-center justify-center w-24 h-24 bg-red-50 rounded-full hover:bg-red-100 transition-all cursor-pointer border-4 border-red-100 hover:border-red-200 shadow-sm"
                                    >
                                        <Mic size={40} className="text-red-500 group-hover:scale-110 transition-transform" />
                                    </button>
                                ) : (
                                    <button 
                                        onClick={stopRecording}
                                        className="group relative flex items-center justify-center w-24 h-24 bg-slate-50 rounded-full hover:bg-slate-100 transition-all cursor-pointer border-4 border-slate-100 hover:border-slate-200 shadow-sm"
                                    >
                                        <Square size={36} className="text-slate-700 fill-slate-700 group-hover:scale-95 transition-transform" />
                                    </button>
                                )}
                                <p className="mt-4 text-slate-500 font-medium">
                                    {isRecording ? t('voiceRecorder.recording') : t('voiceRecorder.clickToStart')}
                                </p>
                            </div>
                        ) : (
                            // RESULT VIEW (PLAYBACK & DOWNLOAD)
                            <div className="w-full max-w-md animate-fade-in">
                                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-6">
                                    <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">{t('voiceRecorder.preview')}</h3>
                                    <audio controls src={audioUrl} className="w-full shadow-sm rounded-lg" />
                                </div>

                                <div className="flex gap-4">
                                    <a 
                                        href={audioUrl}
                                        download={`recording_${new Date().getTime()}${getFileExtension()}`}
                                        className="flex-1 flex items-center justify-center px-6 py-3 bg-brand-orange text-white font-bold rounded-md hover:bg-orange-600 transition shadow-sm"
                                    >
                                        <Download className="mr-2" size={20} /> {t('voiceRecorder.download')}
                                    </a>
                                    
                                    <button 
                                        onClick={resetRecording}
                                        className="px-6 py-3 bg-white text-slate-600 border border-slate-300 font-bold rounded-md hover:bg-slate-50 transition shadow-sm flex items-center"
                                        title={t('voiceRecorder.reset')}
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-8 bg-red-50 border border-red-200 p-4 rounded-lg flex items-center text-red-700">
                                <AlertCircle className="mr-2 flex-shrink-0" size={20} />
                                <span>{error}</span>
                            </div>
                        )}
                        
                        {isRecording && (
                            <div className="mt-8 flex items-center text-sm text-red-500">
                                <span className="relative flex h-3 w-3 mr-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                </span>
                                {t('voiceRecorder.recordingLive')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceRecorder;